import styled from "styled-components"
import { useDispatch, useSelector } from "react-redux"
import { Tooltip as ReactTooltip } from "react-tooltip"
import Portal from "react-overlays/Portal"
import { IRowNode } from "ag-grid-community"
import { buildLaborRateMatchContextParams } from "../../../rate-tables/utils"
import { useRowData } from "../../../common/ag-grid-ts-utils"
import { selectLaborRatesById } from "../../../rate-tables/selectors"
import { AppDispatch, BasicTkTimeType, LaborRateMatch, ReduxState, TimekeepingEntryModalRowData, TkTimeType, tUserRole } from "../../../common/types"
import {
    colorAttentionBlue10,
    colorAttentionBlue50,
    colorAttentionPurple10,
    colorAttentionPurple50,
    colorFunctionalGray10,
    colorFunctionalGray50,
    fontSizeS,
    fontSizeXs,
    spacingXs,
} from "@rhumbix/rmbx_design_system_web"
import { zIndex } from "../../../common/styles"
import { useState, useEffect, useRef } from "react"
import { referenceableDataSelector } from "../../../selectors"
import { checkLaborRateMatch } from "../../../rate-tables/actions"
import { tEntitySelection } from "../../../cached-data/types"
import { selectTkModifierPublicApiFieldNameBySlug } from "../../../tk-modifiers/selectors"

/**
 * Props for the LaborRateMatchBadge component.
 */
interface LaborRateMatchBadgeProps {
    /** The AG Grid row node - use node.data to get current row data */
    node: IRowNode
    /** The time type to check for rate matches: ST, OT, or DT */
    timeType: Exclude<TkTimeType, "TT">
    /** When true, skip clientside matching (e.g. in the history table). Saved rates are still shown. */
    skipClientsideMatch?: boolean
}

/** User roles that are allowed to see the rate dollar amount in the badge tooltip. */
const RATE_AMOUNT_VISIBLE_ROLES: tUserRole[] = ["ADMIN", "PAYROLL_ADMIN", "PM"]

/**
 * Returns the rate amount formatted as a dollar string if the user's role is allowed to see it.
 * Rate values are stored in cents, so this converts to dollars for display.
 *
 * @param userRole - The current user's role
 * @param baseRateCents - The rate's base_rate value in cents, or undefined if no rate is available
 * @returns A formatted dollar string (e.g. "$12.50"), or null if the role cannot see rates or no rate is available
 */
export const formatRateAmountForRole = (userRole: tUserRole, baseRateCents: number | undefined): string | null => {
    if (!RATE_AMOUNT_VISIBLE_ROLES.includes(userRole) || baseRateCents === undefined) {
        return null
    }
    return `$${(baseRateCents / 100).toFixed(2)}`
}

/** Possible sources for a rate match. */
type RateMatchSource = "clientside_match" | "clientside_override" | "saved_match" | "saved_override" | "no_match"

/**
 * A rate ID and where it came from so the UI can render something useful.
 * rateId is absent when source is "no_match" (matching ran but found no rate).
 */
interface RateMatchDisplayInfo {
    /** The source of the rate match. */
    source: RateMatchSource
    /** The rate ID to use for UI — undefined when source is "no_match" */
    rateId?: number
}

/**
 * Determines the match type and rate ID based on priority rules:
 *
 * 1. Clientside override (number): User picked a specific rate → clientside_override
 * 2. Clientside override (null): User reset, auto-match masks saved → clientside_match
 * 3. Saved override (always sticky): → saved_override
 * 4. Saved auto-match, context unchanged: → saved_match
 * 5. Saved no-match, context unchanged: → no_match
 * 6. Default auto-match: → clientside_match
 *
 * @param overrideRateId - The clientside override LaborRate ID (number), reset (null), or untouched (undefined)
 * @param cachedRateMatch - The cached LaborRateMatch record looked up by savedRateMatchId, if available
 * @param autoMatchRateId - The clientside auto-matched LaborRate ID, if any
 * @param hasContextChanged - Whether row context fields have changed since initial load, making saved matches stale
 * @returns The rate match display info, or null if no rate match should be shown
 */
export const getRateMatchDisplayInfo = (
    overrideRateId: number | null | undefined,
    cachedRateMatch: LaborRateMatch | undefined,
    autoMatchRateId: number | null,
    hasContextChanged: boolean
): RateMatchDisplayInfo | null => {
    // Case 1: User explicitly selected a rate via the reprice workflow
    if (typeof overrideRateId === "number") {
        return { source: "clientside_override", rateId: overrideRateId }
    }

    // Case 2: User chose to reset the assigned rate — auto-match takes over and masks any saved match
    if (overrideRateId === null) {
        return autoMatchRateId ? { source: "clientside_match", rateId: autoMatchRateId } : null
    }

    if (cachedRateMatch) {
        // Case 3: Saved override is permanently sticky — it represents an explicit user choice
        // that should survive context changes (employee, project, cost code, date).
        if (cachedRateMatch.labor_rate_override) {
            return { source: "saved_override", rateId: cachedRateMatch.labor_rate_override }
        }
        // Cases 4/5: Saved auto-match and saved no-match are only authoritative when context
        // is unchanged. If context has changed, the server will assign new rates on save, so
        // these results are stale and we fall through to the clientside auto-match.
        if (!hasContextChanged) {
            if (cachedRateMatch.labor_rate) {
                return { source: "saved_match", rateId: cachedRateMatch.labor_rate }
            }
            return { source: "no_match" }
        }
    }

    // Case 6: No authoritative saved match — show clientside auto-match if available
    if (autoMatchRateId) {
        return { source: "clientside_match", rateId: autoMatchRateId }
    }

    return null
}

/**
 * Returns the clientside override LaborRate ID for the given time type from the row data.
 *
 * @param rowData - The timekeeping entry modal row data
 * @param timeType - ST, OT, or DT
 * @returns number if user selected a rate, null if user reset, undefined if untouched
 */
const getOverrideRateId = (rowData: TimekeepingEntryModalRowData | undefined, timeType: BasicTkTimeType): number | null | undefined => {
    if (!rowData) return undefined
    if (timeType === "ST") return rowData.labor_rate_st_override
    if (timeType === "OT") return rowData.labor_rate_ot_override
    if (timeType === "DT") return rowData.labor_rate_dt_override
    return undefined
}

/**
 * Returns the saved LaborRateMatch ID for the given time type from the row data.
 *
 * @param rowData - The timekeeping entry modal row data
 * @param timeType - ST, OT, or DT
 * @returns The saved LaborRateMatch ID, or undefined if none
 */
const getSavedRateMatchId = (rowData: TimekeepingEntryModalRowData | undefined, timeType: Exclude<TkTimeType, "TT">): number | undefined => {
    if (!rowData) return undefined
    if (timeType === "ST") return rowData.rate_match_st
    if (timeType === "OT") return rowData.rate_match_ot
    if (timeType === "DT") return rowData.rate_match_dt
    return undefined
}

/**
 * Badge component that displays predicted or applied labor rate information.
 * Shows a color-coded badge with a tooltip containing the Rate Table and Labor Rate names.
 *
 * Badge state priority (highest to lowest):
 * 1. Clientside override (user selected a rate)
 * 2. Clientside reprice masking saved match (use clientside auto-match)
 * 3. Saved rate match override
 * 4. Saved rate match (auto-matched)
 * 5. Default clientside auto-match
 */
const LaborRateMatchBadge: React.FC<LaborRateMatchBadgeProps> = ({ node, timeType, skipClientsideMatch }) => {
    const dispatch = useDispatch() as AppDispatch
    const cachedLaborRatesById = useSelector(selectLaborRatesById)
    const userRole = useSelector((state: ReduxState) => state.current_user.user_role)
    const referenceableData: tEntitySelection = useSelector(referenceableDataSelector)
    const publicApiFieldNameBySlug = useSelector(selectTkModifierPublicApiFieldNameBySlug)
    const [autoMatchRateId, setAutoMatchRateId] = useState<number | null>(null)
    // Trigger re-renders when AG-Grid updates the node data.
    const rowData = useRowData(node) as TimekeepingEntryModalRowData | undefined

    // Row data changes a lot, so we'll keep track of just the fields we're interested in to better detect
    // when matching needs to be re-run.
    const contextParams = rowData
        ? buildLaborRateMatchContextParams(rowData, referenceableData, publicApiFieldNameBySlug)
        : { employeeId: null, projectId: null, costCodeId: null, modifiersString: "", entryDate: "" }
    const { employeeId, projectId, costCodeId, modifiersString, entryDate } = contextParams
    // Track the initial context field values so we can detect when the row has been edited.
    // When context changes, saved auto-matches become stale (server will re-assign on save),
    // but saved overrides remain valid.
    const initialContextRef = useRef(contextParams)
    const hasContextChanged =
        initialContextRef.current.employeeId !== employeeId ||
        initialContextRef.current.projectId !== projectId ||
        initialContextRef.current.costCodeId !== costCodeId ||
        initialContextRef.current.modifiersString !== modifiersString ||
        initialContextRef.current.entryDate !== entryDate

    // When it looks like relevant row data has changed, re-run matching.
    // Skip clientside matching for historical entries (e.g. audit history table) since they are read-only
    // and only saved rates are relevant.
    useEffect(
        () => {
            if (skipClientsideMatch) return
            const runMatching = async () => {
                const matchId = await dispatch(checkLaborRateMatch({ employeeId, projectId, costCodeId, modifiersString, entryDate }, timeType))
                setAutoMatchRateId(matchId)
            }
            runMatching()
        },
        // These are all primitive values so that the effect only runs when they change.
        // Avoid tracking objects that may get rebuilt every render here.
        [employeeId, projectId, costCodeId, modifiersString, entryDate, timeType, skipClientsideMatch]
    )

    const overrideRateId = getOverrideRateId(rowData, timeType)
    const savedRateMatchId = getSavedRateMatchId(rowData, timeType)
    const cachedRateMatch = savedRateMatchId ? (referenceableData.laborRateMatches[savedRateMatchId] as LaborRateMatch | undefined) : undefined
    const displayInfo = getRateMatchDisplayInfo(overrideRateId, cachedRateMatch, autoMatchRateId, hasContextChanged)

    if (!displayInfo) {
        return null
    }

    const { source, rateId } = displayInfo
    const cachedRate = rateId ? cachedLaborRatesById[rateId] : undefined
    const autoMatchRate = autoMatchRateId ? cachedLaborRatesById[autoMatchRateId] : undefined
    const showVersionNotice = source === "saved_match" && cachedRate && autoMatchRate && cachedRate.version !== autoMatchRate.version
    const rateName = source === "no_match" ? "No rates matched" : cachedRate?.name ?? "Rate applied"
    const rateTableName = cachedRate?.rate_table?.name ?? ""
    const rateAmountDisplay = formatRateAmountForRole(userRole, cachedRate?.base_rate)
    const tooltipId = `labor-rate-match-${rowData?.gridId ?? node.id ?? "unknown"}-${timeType}`

    const rateStatusLabel = (() => {
        if (source === "saved_override") return "Override"
        if (source === "saved_match" || source === "no_match") return "Saved"
        if (source === "clientside_override") return "Override (pending)"
        return "Pending"
    })()

    return (
        <>
            <MatchBadge source={source} data-tooltip-id={tooltipId} />
            <Portal container={document.body}>
                <ReactTooltip id={tooltipId} place="top" float={true} variant="dark" style={{ zIndex: zIndex.Tooltip }}>
                    <TooltipContent>
                        <RateName>{rateName}</RateName>
                        {rateTableName ? <RateTableName>{rateTableName}</RateTableName> : null}
                        {rateAmountDisplay ? <RateAmount>{rateAmountDisplay}</RateAmount> : null}
                        <RateStatus>{rateStatusLabel}</RateStatus>
                        {showVersionNotice ? <VersionNotice>New version available</VersionNotice> : null}
                    </TooltipContent>
                </ReactTooltip>
            </Portal>
        </>
    )
}

interface MatchBadgeProps extends React.HTMLAttributes<HTMLDivElement> {
    source: RateMatchSource
}

const BADGE_STYLES: Record<RateMatchSource, { background: string; border: string; color: string }> = {
    clientside_match: {
        background: colorAttentionBlue10,
        border: `1px dashed ${colorAttentionBlue50}`,
        color: colorAttentionBlue50,
    },
    clientside_override: {
        background: colorAttentionPurple10,
        border: `1px dashed ${colorAttentionPurple50}`,
        color: colorAttentionPurple50,
    },
    saved_match: {
        background: colorAttentionBlue10,
        border: `1px solid ${colorAttentionBlue50}`,
        color: colorAttentionBlue50,
    },
    saved_override: {
        background: colorAttentionPurple10,
        border: `1px solid ${colorAttentionPurple50}`,
        color: colorAttentionPurple50,
    },
    no_match: {
        background: colorFunctionalGray10,
        border: `1px solid ${colorFunctionalGray50}`,
        color: colorFunctionalGray50,
    },
}

/**
 * A circular badge indicator for labor rate match status.
 * Styling differs depending on the source of the rate match.
 *
 * @param source - The source of the rate match
 */
const MatchBadge: React.FC<MatchBadgeProps> = ({ source, ...rest }) => (
    <MatchBadgeHitArea {...rest}>
        <StyledMatchBadge source={source}>
            <span>$</span>
        </StyledMatchBadge>
    </MatchBadgeHitArea>
)

/** Square hit area wrapper that makes the tooltip trigger on the full bounding box, not just the circle. */
const MatchBadgeHitArea = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-style: normal;
`

const StyledMatchBadge = styled.div<{ source: RateMatchSource }>`
    width: 22px;
    height: 22px;
    border-radius: 50%;
    background-color: ${({ source }) => BADGE_STYLES[source].background};
    border: ${({ source }) => BADGE_STYLES[source].border};
    color: ${({ source }) => BADGE_STYLES[source].color};
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: ${fontSizeXs};
    font-weight: 400;
    line-height: 1;
    box-sizing: border-box;
    pointer-events: none;

    span {
        display: block;
        line-height: 1;
        transform: translateY(0.25px);
    }
`

const TooltipContent = styled.div`
    display: flex;
    flex-direction: column;
`

const VersionNotice = styled.span`
    font-size: ${fontSizeXs};
    font-style: italic;
`

const RateStatus = styled.span`
    font-size: ${fontSizeXs};
    font-style: italic;
    margin-top: ${spacingXs};
`

const RateAmount = styled.span`
    font-size: ${fontSizeXs};
    margin-top: ${spacingXs};
`

const RateTableName = styled.span`
    font-size: ${fontSizeXs};
`

const RateName = styled.span`
    font-size: ${fontSizeS};
`

export default LaborRateMatchBadge
